import React, { useContext } from 'react';
import {
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Typography,
    Box,
} from '@mui/material';
import { LocalizeContext, LocalizeContextProps, Translate } from 'react-localize-redux';
import { appointmentTypeLabel, AppointmentTypeRef } from '../utils/outpatientAppointmentTypes';

interface ProductivityStats {
    totalDoctors: number;
    totalAppointments: number;
    totalUniquePatients: number;
    /** AppointmentTypeRef.key -> attended appointments of that type (SHER-1049). */
    totalsByType: Record<string, number>;
}

interface DoctorData {
    researcher_uuid: string;
    doctor_name: string;
    doctor_surnames: string;
    department_name: string;
    total_appointments: number;
    unique_patients: number;
    /** AppointmentTypeRef.key -> attended appointments; only types present appear. */
    appointments_by_type: Record<string, number>;
}

interface ProductivityTableProps {
    stats: ProductivityStats;
    data: DoctorData[];
    /** The type columns, in the order the API sent them (SHER-1049). */
    appointmentTypes: AppointmentTypeRef[];
    department: string;
    /** @deprecated Free consultations removed (SHER-561); kept for ChartFactory call sites */
    showOptionalColumns?: boolean;
    title?: string;
    type?: string;
}

const StatsCard = ({ title, value }: { title: React.ReactNode; value: number }) => (
    <Card className="h-full">
        <CardContent>
            <Typography color="textSecondary" gutterBottom>
                {title}
            </Typography>
            <Typography variant="h5" component="div">
                {value}
            </Typography>
        </CardContent>
    </Card>
);

const ProductivityTable = ({
    stats,
    data = [],
    appointmentTypes = [],
}: ProductivityTableProps) => {
    // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
    // package's typings. See analyticsTranslate.sher828.test.tsx.
    const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
    const rows = Array.isArray(data) ? data : [];
    const totals = {
        total_appointments: rows.reduce((sum, row) => sum + (row.total_appointments || 0), 0),
        unique_patients: rows.reduce((sum, row) => sum + (row.unique_patients || 0), 0),
    };

    return (
        <div className="space-y-2">
            <Box sx={{ mb: 1 }}>
                <Typography variant="h6">
                    <Translate id="hospital.analytics.clinical_activity.section_title" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <Translate id="hospital.analytics.clinical_activity.section_subtitle" />
                </Typography>
            </Box>

            <Grid container spacing={2} className="mb-4">
                <Grid item xs={12} sm={6} md={4}>
                    <StatsCard
                        title={<Translate id="hospital.analytics.clinical_activity.professionals" />}
                        value={stats?.totalDoctors || 0}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatsCard
                        title={<Translate id="hospital.analytics.clinical_activity.honored" />}
                        value={stats?.totalAppointments || 0}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatsCard
                        title={<Translate id="hospital.analytics.clinical_activity.unique_patients" />}
                        value={stats?.totalUniquePatients || 0}
                    />
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.professional" />
                                </strong>
                            </TableCell>
                            <TableCell>
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.service" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.honored" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.unique_patients" />
                                </strong>
                            </TableCell>
                            {appointmentTypes.map((type) => (
                                <TableCell key={type.key} align="right">
                                    <strong>{appointmentTypeLabel(type, translate, 'long')}</strong>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow
                                key={`${row.researcher_uuid}-${row.department_name}`}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">
                                    {`${row.doctor_name || ''} ${row.doctor_surnames || ''}`.trim()}
                                </TableCell>
                                <TableCell>{row.department_name}</TableCell>
                                <TableCell align="right">{row.total_appointments}</TableCell>
                                <TableCell align="right">{row.unique_patients}</TableCell>
                                {appointmentTypes.map((type) => (
                                    <TableCell key={type.key} align="right">
                                        {row.appointments_by_type?.[type.key] || 0}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {rows.length > 0 && (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <strong>
                                        <Translate id="hospital.analytics.clinical_activity.total" />
                                    </strong>
                                </TableCell>
                                <TableCell align="right"><strong>{totals.total_appointments}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.unique_patients}</strong></TableCell>
                                {appointmentTypes.map((type) => (
                                    <TableCell key={type.key} align="right">
                                        <strong>{stats?.totalsByType?.[type.key] || 0}</strong>
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ProductivityTable;
